import React, { useEffect, useState } from "react";
import "./servicePage.css";
import "../NewHome/newhome.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import services from "../../Static";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { transition } from "../../../utils/transition";
import { fadeIn } from "../../../utils/variants";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const ServicePage = () => {
  const [payload, setPayload] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const foundObject = services.find(
      (service) => service.id === String(id)
    );
    if (foundObject) {
      setPayload(foundObject);
    } else {
      console.error(`No object found with id ${id}`);
    }
  }, [id]);

  if (!payload) return null;

  const displayTitle = payload.title2 || payload.title;
  const description =
    payload.paragraph ||
    "Scaftech delivers integrated engineering, lifting, inspection, and training services that keep your critical assets safe, compliant, and high-performing across the full lifecycle.";

  // Simple “other services” rail
  const related = services
    .filter((s) => s.id !== payload.id)
    .slice(0, 4);

  return (
    <div className="servicePageDiv min-h-screen bg-[#020617] text-slate-50">
      {/* Futuristic background layers */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,#0b1220_1px,transparent_1px),linear-gradient(to_bottom,#0b1220_1px,transparent_1px)] [background-size:46px_46px]" />
      <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-[#00ad0f]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#2563eb]/30 blur-3xl" />

      {/* TOP / HERO */}
      <section className="relative border-b border-white/10 pt-28 md:pt-32 lg:pt-36">
        <div className="container2 mx-auto max-w-6xl px-5 pb-10 md:pb-14">
          {/* Top meta row */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4 text-[11px] uppercase tracking-[0.28em] text-slate-400">
            <div className="flex flex-wrap items-center gap-3 mt-10">

              <span className="h-[1px] w-8 bg-slate-600" />

              <span className="inline-flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full shadow-[0_0_12px_rgba(34,197,94,0.8)]"
                  style={{ background: BRAND.green }}
                />
                Scaftech Nigeria • Service Control Module
              </span>
            </div>

            <div className="flex items-center gap-3 text-[10px]">
              <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-emerald-200">
                Live • Operational
              </span>
              <span className="hidden sm:inline text-slate-500">
                Service ID: {payload.id}
              </span>
            </div>
          </div>

          {/* Main hero content */}
          <div className="grid gap-8 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)] md:items-stretch">
            {/* Left: text / info */}
            <motion.div
              variants={fadeIn("Up")}
              transition={transition()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative rounded-3xl border border-white/10 bg-[#020819]/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8"
            >
              {/* Accent bar */}
              <div className="absolute inset-x-8 top-0 h-[3px] rounded-b-full bg-gradient-to-r from-[#00ad0f] via-white/40 to-[#062b6c]" />

              <div className="mb-5 flex items-center justify-between text-[11px] uppercase tracking-[0.26em] text-slate-400">
                <span className="flex items-center gap-2">
                  <span className="text-slate-500">Service Module</span>
                  <span className="text-slate-200">
                    0{payload.id} • {displayTitle}
                  </span>
                </span>
                <span className="hidden sm:inline text-slate-500">
                  ISO 9001:2015
                </span>
              </div>

              <h1 className="mb-3 text-3xl font-semibold uppercase text-slate-50 md:text-4xl lg:text-[2.45rem] lg:leading-tight">
                <span className="block text-sm font-medium tracking-[0.32em] text-slate-400">
                  Asset Integrity • Lifting • Training
                </span>
                <span className="mt-2 inline-block bg-gradient-to-r from-[#00ad0f] to-[#7fffb0] bg-clip-text text-transparent">
                  {displayTitle}
                </span>
              </h1>

              <p className="mb-6 max-w-xl text-sm leading-relaxed text-slate-200 md:text-[0.98rem]">
                {description}
              </p>

              {/* Telemetry strip */}
              <div className="mb-6 grid gap-3 text-[11px] md:grid-cols-3">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="uppercase tracking-[0.22em] text-emerald-300">
                      Availability
                    </span>
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]" />
                  </div>
                  <p className="text-xs text-emerald-100">
                    Deployed across oil &amp; gas, marine, and construction hubs.
                  </p>
                </div>

                <div className="rounded-2xl border border-sky-400/30 bg-sky-400/5 px-3 py-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="uppercase tracking-[0.22em] text-sky-300">
                      Integrity
                    </span>
                    <ArrowForwardIcon className="h-3.5 w-3.5 text-sky-200" />
                  </div>
                  <p className="text-xs text-sky-100">
                    Engineered to reduce downtime and maximize asset lifespan.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-500/40 bg-slate-800/60 px-3 py-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="uppercase tracking-[0.22em] text-slate-300">
                      Compliance
                    </span>
                    <span className="text-[10px] text-slate-300">
                      API • ISO • ILO
                    </span>
                  </div>
                  <p className="text-xs text-slate-200">
                    Driven by global standards and local regulatory frameworks.
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact-us"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] text-[#020617] transition-all hover:-translate-y-[1px] hover:bg-white"
                >
                  <span>Schedule a Session</span>
                </a>

                <a
                  href="/services"
                  className="inline-flex items-center gap-2 rounded-full border border-[#00ad0f]/70 bg-transparent px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] text-[#bbffcc] backdrop-blur-sm transition-all hover:bg-[#00ad0f] hover:text-white"
                >
                  <span className="h-2.5 w-2.5 rounded-full bg-[#00ad0f] shadow-[0_0_10px_rgba(0,173,15,0.9)]" />
                  <span>View All Services</span>
                </a>
              </div>
            </motion.div>

            {/* Right: visual “live feed” */}
            <motion.div
              variants={fadeIn("Up")}
              transition={transition()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="md:h-full"
            >
              <div className="relative h-56 overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 shadow-[0_24px_80px_rgba(0,0,0,0.8)] md:h-full">
                {/* header strip */}
                <div className="absolute inset-x-5 top-4 flex items-center justify-between text-[11px] uppercase tracking-[0.26em] text-slate-300">
                  <span>Visual Feed • Field Capture</span>
                  <span className="flex items-center gap-1 text-[10px]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#fbbf24] shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                    Recording
                  </span>
                </div>

                <img
                  src={payload?.img2 || payload?.img}
                  alt={displayTitle}
                  className="absolute inset-0 h-full w-full object-cover"
                />

                {/* overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,rgba(15,23,42,0.9)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.9)_1px,transparent_1px)] [background-size:32px_32px]" />

                {/* footer meta */}
                <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between text-[11px] text-slate-100">
                  <div className="max-w-xs truncate">
                    <span className="block text-[10px] uppercase tracking-[0.26em] text-slate-400">
                      Scaftech Service • Active
                    </span>
                    <span className="truncate text-sm">
                      {displayTitle}
                    </span>
                  </div>
                  <div className="flex flex-col items-end text-[10px] text-slate-300">
                    <span>Module #{payload.id}</span>
                    <span className="text-[9px] text-slate-500">
                      Synced • 99.9% Uptime
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BODY / DETAILS */}
      <section className="relative py-10 md:py-14">
        <div className="container2 mx-auto max-w-5xl px-5">
          <motion.div
            variants={fadeIn("Up")}
            transition={transition()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative rounded-3xl border border-slate-700/60 bg-slate-900/60 p-6 text-[0.97rem] leading-[1.9] text-slate-100 shadow-[0_18px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8"
          >
            <div className="mb-4 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] text-slate-400">
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Technical Overview
              </span>
              <span className="text-[10px] text-slate-500">
                Export-ready • PDF / DOC
              </span>
            </div>

            {/* your rich content from Static.js */}
            <div className="prose prose-invert max-w-none prose-headings:text-slate-50 prose-p:text-slate-100 prose-strong:text-emerald-300 prose-li:marker:text-emerald-400">
              {payload.body}
            </div>
          </motion.div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      {related.length > 0 && (
        <section className="relative border-t border-slate-800/80 bg-[#020617] pb-14 pt-10">
          <div className="container2 mx-auto max-w-6xl px-5">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.26em] text-slate-400">
                  Other Service Modules
                </p>
                <h2 className="text-lg font-semibold text-slate-50 md:text-xl">
                  Explore more from Scaftech’s integrated service deck.
                </h2>
              </div>
              <a
                href="/services"
                className="hidden text-[11px] uppercase tracking-[0.24em] text-emerald-300 underline-offset-4 hover:underline md:inline"
              >
                View full catalogue
              </a>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {related.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => navigate(`/services/${service.id}`)}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/70 text-left shadow-[0_14px_40px_rgba(0,0,0,0.7)] transition-transform hover:-translate-y-1 hover:border-emerald-400/60"
                >
                  <div className="relative h-24 overflow-hidden">
                    <img
                      src={service.img2 || service.img}
                      alt={service.title2 || service.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <span className="absolute bottom-2 left-3 rounded-full bg-black/50 px-2 py-[3px] text-[9px] uppercase tracking-[0.22em] text-slate-200">
                      Module #{service.id}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 px-3 py-3">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">
                      {service.title2 || service.title}
                    </p>
                    {service.paragraph && (
                      <p className="line-clamp-3 text-[12px] text-slate-300">
                        {service.paragraph}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ServicePage;
